import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CartonContents } from '@app/store/sorting/models';
import { CartonContentsDto } from '@app/store/sorting/models/carton-contents.dto';
import { WarehouseError } from '@app/store/shared/models';
import { Box, BoxDetailsDto, BoxDto } from '../models';
import { mapDto } from '@shared/operators';
import { ApiResponse, hasMore, PaginatedApiResponse } from '@shared/models/api-response';

const PAGE_SIZE = 20;

@Service()
export class HttpService {
    private readonly httpClient = inject(HttpClient);

    loadPlacedBoxes(stationCode: string, page: number): Observable<{ boxes: Box[]; hasMore: boolean }> {
        return this.httpClient.get<PaginatedApiResponse<BoxDetailsDto[]>>(`stations/${stationCode}/cartons`, { params: { page, limit: PAGE_SIZE } }).pipe(
            mapDto((response) => ({
                boxes: response.data.map((dto) => new Box(dto)),
                hasMore: hasMore(response),
            })),
        );
    }

    loadBox(code: string): Observable<Box> {
        return this.httpClient
            .skipErrorMessageInterceptor()
            .get<ApiResponse<BoxDto>>(`cartons/${code}`)
            .pipe(mapDto((response) => new Box(response.data)));
    }

    lockBox(code: string): Observable<CartonContents> {
        return this.httpClient.post<ApiResponse<CartonContentsDto>>(`cartons/${code}/lock`, {}).pipe(
            mapDto((response) => new CartonContents(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('close-box.lock.error')))),
        );
    }

    unlockBox(code: string): Observable<void> {
        return this.httpClient
            .skipErrorMessageInterceptor()
            .post(`cartons/${code}/unlock`, {})
            .pipe(map(() => undefined));
    }

    reduceItem(code: string, ean: string): Observable<CartonContents> {
        return this.httpClient.delete<ApiResponse<CartonContentsDto>>(`cartons/${code}/items`, { body: { ean, quantity: 1 } }).pipe(
            mapDto((response) => new CartonContents(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('close-box.box.reduce.error')))),
        );
    }

    increaseItem(code: string, ean: string): Observable<CartonContents> {
        return this.httpClient.patch<ApiResponse<CartonContentsDto>>(`cartons/${code}/items`, { ean, quantity: 1 }).pipe(
            mapDto((response) => new CartonContents(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('close-box.box.add.error')))),
        );
    }

    closeBox(code: string): Observable<CartonContents> {
        return this.httpClient.post<ApiResponse<CartonContentsDto>>(`cartons/${code}/close`, {}).pipe(
            mapDto((response) => new CartonContents(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('close-box.close.error')))),
        );
    }
}
